"use client";

import {
  Children,
  forwardRef,
  isValidElement,
  useEffect,
  useMemo,
} from "react";
import { cn } from "@/lib/utils";
import type { RotationalCarouselContentProps } from "./RotationalCarousel.types";
import { useRotationalCarousel } from "./RotationalCarouselContext";
import { RotationalCarouselItem } from "./RotationalCarouselItem";

/**
 * Viewport wrapper for carousel slides. Fulfills two responsibilities:
 *
 * 1. **Height synchronization** — sets its own CSS `height` to match the
 *    center card's rendered height (sourced from context). This prevents the
 *    container from collapsing while its `<RotationalCarouselItem>` children
 *    are `position: absolute` and therefore out of normal flow.
 *
 * 2. **Item counting** — counts the direct `<RotationalCarouselItem>` children
 *    and reports the total to context. Items need this count to calculate their
 *    circular slot offsets for the infinite-loop wrapping behavior.
 *
 * Must be placed directly inside `<RotationalCarousel>`.
 */
const RotationalCarouselContent = forwardRef<
  HTMLDivElement,
  RotationalCarouselContentProps
>(({ children, className }, ref) => {
  const { cardHeight, setTotalItems } = useRotationalCarousel();

  // Count only RotationalCarouselItem children — other elements (e.g. nav
  // buttons or decorative wrappers) must not skew the total used for circular
  // offset calculations, which would cause items to mis-position themselves.
  const count = useMemo(
    () =>
      Children.toArray(children).filter(
        (child) =>
          isValidElement(child) && child.type === RotationalCarouselItem,
      ).length,
    [children],
  );

  // Propagate the count to context after every render where it changed.
  useEffect(() => {
    setTotalItems(count);
  }, [count, setTotalItems]);

  return (
    // height is driven by cardHeight from context (measured by the center item).
    // Without an explicit height the container would be 0px tall because all
    // children are absolutely positioned.
    <div
      ref={ref}
      className={cn("tw-relative tw-w-full", className)}
      style={{ height: cardHeight }}
    >
      {children}
    </div>
  );
});

RotationalCarouselContent.displayName = "RotationalCarouselContent";

export { RotationalCarouselContent };
